import { ICoreContext, msdyn365Commerce } from '@msdyn365-commerce/core';
import { vSI_CallGenericRestAPIHandlerAsync } from '../vsi-add-charges-extension/DataActionExtension.g';

import { mapPaymentError } from './payment-errors/payment-error.mapper';

interface ICheckoutErrorLike {
    name?: string;
    code?: string;
    message?: string;
    status?: string;
    statusCode?: number;
}

interface INotifyCheckoutErrorByDotDigitalOptions {
    forceNotification?: boolean;
    recipientEmailsOverride?: string[];
    customerEmailOverride?: string;
}

interface IDotDigitalClientConfig {
    notificationEnabled: boolean;
    recipientEmails: string[];
    contactsPath: string;
    enrollmentPath: string;
    programId: string;
}

const DEFAULT_DOTDIGITAL_CONTACTS_PATH = 'v2/contacts';
const DEFAULT_DOTDIGITAL_ENROLLMENT_PATH = 'v2/programs/enrolments';
const DOTDIGITAL_REQUEST_HEADER = "{'cache-control':'no-cache' , 'Accept':'application/json'}";
const DEFAULT_DOTDIGITAL_PROGRAM_ID = 480817;

const normalizeRecipientEmails = (recipientConfig: unknown): string[] => {
    if (Array.isArray(recipientConfig)) {
        return recipientConfig.filter((email): email is string => typeof email === 'string' && !!email.trim());
    }

    if (typeof recipientConfig === 'string' && recipientConfig.trim()) {
        return recipientConfig
            .split(',')
            .map(email => email.trim())
            .filter(Boolean);
    }

    return [];
};

const getClientConfig = (context: ICoreContext): IDotDigitalClientConfig => {
    const appConfig = context?.request?.app?.config;

    return {
        notificationEnabled: appConfig?.checkoutErrorDotDigitalNotificationEnabled === true,
        recipientEmails: normalizeRecipientEmails(appConfig?.checkoutErrorDotDigitalRecipientEmails),
        contactsPath: DEFAULT_DOTDIGITAL_CONTACTS_PATH,
        enrollmentPath: DEFAULT_DOTDIGITAL_ENROLLMENT_PATH,
        programId: (appConfig?.checkoutErrorDotDigitalProgramId as string) || '480817'
    };
};

const parseJsonSafely = (value?: string): any => {
    if (!value) {
        return undefined;
    }

    try {
        return JSON.parse(value);
    } catch {
        return undefined;
    }
};

const resolveContactId = (responsePayload: any): number | undefined => {
    const candidate = responsePayload?.id ?? responsePayload?.Id ?? responsePayload?.contactId ?? responsePayload?.ContactId;
    const numericValue = Number(candidate);
    return Number.isFinite(numericValue) ? numericValue : undefined;
};

// const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> => {
//     return Promise.race([
//         promise,
//         new Promise<T>((_, reject) =>
//             setTimeout(() => reject(new Error(`[Dotdigital] ${label} timed out after ${timeoutMs}ms`)), timeoutMs)
//         )
//     ]);
// };

export const notifyCheckoutErrorByDotDigital = async (
    context: ICoreContext,
    error: unknown,
    source: string,
    options?: INotifyCheckoutErrorByDotDigitalOptions
): Promise<void> => {
    if (!msdyn365Commerce.isBrowser) {
        console.warn('[Dotdigital] Skipped: not running in browser context.');
        return;
    }

    const config = getClientConfig(context);

    if (options?.forceNotification !== true && config.notificationEnabled !== true) {
        console.warn('[Dotdigital] Skipped: notification disabled in CMS config.');
        return;
    }

    const recipients =
        options?.recipientEmailsOverride && options.recipientEmailsOverride.length > 0
            ? normalizeRecipientEmails(options.recipientEmailsOverride)
            : config.recipientEmails;

    if (recipients.length === 0) {
        console.warn('[Dotdigital] Notification skipped: recipient emails are missing.');
        return;
    }

    const checkoutError = (error ?? {}) as ICheckoutErrorLike;
    const mappedPaymentError = mapPaymentError(checkoutError);

    const dynamicsErrorCode = checkoutError.name || checkoutError.code || '';
    const errorCode =
        mappedPaymentError?.customCode ||
        dynamicsErrorCode ||
        (checkoutError.statusCode ? `CHECKOUT_ERROR_${checkoutError.statusCode}` : 'CHECKOUT_ERROR');

    const customerEmail =
        options?.customerEmailOverride ||
        (context.request.user as { emailAddress?: string } | undefined)?.emailAddress ||
        (context.actionContext.requestContext.user as { emailAddress?: string } | undefined)?.emailAddress ||
        '';
    if (!customerEmail) {
        console.warn('[Dotdigital] Customer email unavailable (guest); sending alert without it.', { source, errorCode });
    }

    if (!config.programId) {
        console.warn('[Dotdigital] Notification skipped: checkoutErrorDotDigitalProgramId is missing.');
        return;
    }

    const callerContext = { callerContext: context.actionContext };

    for (const recipientEmail of recipients) {
        try {
            const contactPayload = {
                email: recipientEmail,
                emailType: 'Html',
                dataFields: [
                    { key: 'EMAIL_ADDRESS', value: customerEmail },
                    { key: 'ERROR_CODE', value: errorCode }
                ]
            };

            const contactResponse = await vSI_CallGenericRestAPIHandlerAsync(
                callerContext,
                'DotDigital',
                'post',
                DOTDIGITAL_REQUEST_HEADER,
                JSON.stringify(contactPayload),
                '',
                config.contactsPath
            );

            const parsedContactResponse = parseJsonSafely(contactResponse?.result);
            const contactId = resolveContactId(parsedContactResponse);
            if (!contactId) {
                console.warn('[Dotdigital] Contact creation did not return contact id.', {
                    source,
                    recipientEmail,
                    customerEmail: customerEmail || 'UNKNOWN_CUSTOMER_EMAIL',
                    errorCode,
                    contactResponse: contactResponse?.result
                });
                continue;
            }

            const parsedProgramId = Number(config.programId);
            const enrollmentPayload = {
                contacts: [contactId],
                programId: Number.isFinite(parsedProgramId) ? parsedProgramId : DEFAULT_DOTDIGITAL_PROGRAM_ID
            };

            const enrollmentResponse = await vSI_CallGenericRestAPIHandlerAsync(
                callerContext,
                'DotDigital',
                'post',
                DOTDIGITAL_REQUEST_HEADER,
                JSON.stringify(enrollmentPayload),
                '',
                config.enrollmentPath
            );

            const enrollmentPayloadResponse = parseJsonSafely(enrollmentResponse?.result);
            if (enrollmentPayloadResponse?.message || enrollmentPayloadResponse?.Message) {
                console.warn('[Dotdigital] Enrollment returned an error response.', {
                    source,
                    recipientEmail,
                    customerEmail: customerEmail || 'UNKNOWN_CUSTOMER_EMAIL',
                    errorCode,
                    enrollmentResponse: enrollmentResponse?.result
                });
                continue;
            }
        } catch (notificationError) {
            console.error('[Dotdigital] Notification chain failed.', {
                source,
                recipientEmail,
                customerEmail: customerEmail || 'UNKNOWN_CUSTOMER_EMAIL',
                errorCode,
                notificationError
            });
        }
    }
};
